<script lang="ts">
  import { Confetti } from "svelte-confetti"
  import { Dice1 as Dice, Plus, Minus } from "lucide-svelte"
  import { Button } from "$lib/components/ui/button"
  import * as Card from "$lib/components/ui/card"
  import { Input } from "$lib/components/ui/input"
  import { Label } from "$lib/components/ui/label"
  import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
  } from "$lib/components/ui/tabs"
  import { Switch } from "$lib/components/ui/switch"
  import { secureRandomIntegers } from "$lib/utils/secureRandom"

  // Standard dice types
  const standardDice = [
    { name: "4-sided", sides: 4, icon: "4" },
    { name: "6-sided", sides: 6, icon: "6" },
    { name: "8-sided", sides: 8, icon: "8" },
    { name: "10-sided", sides: 10, icon: "10" },
    { name: "12-sided", sides: 12, icon: "12" },
    { name: "20-sided", sides: 20, icon: "20" },
    { name: "100-sided", sides: 100, icon: "100" },
  ]

  // State for dice rolls
  let diceToRoll = $state<
    Array<{ type: string; sides: number; quantity: number }>
  >([{ type: "d6", sides: 6, quantity: 2 }])
  let rollResults = $state<
    Array<{ type: string; sides: number; results: number[]; total: number }>
  >([])
  let isRolling = $state(false)
  let showConfetti = $state(false)
  let customSides = $state(30)
  let activeTab = $state("standard")
  let rollSound = $state(true)
  let rollSpeed = $state(1000) // milliseconds
  let error = $state<string | null>(null)
  let rollHistory = $state<
    Array<{ timestamp: string; dice: string; results: number[]; total: number }>
  >([])
  let showHistory = $state(false)

  // Function to add dice to roll
  function addDice(type: string, sides: number) {
    const existingIndex = diceToRoll.findIndex((d) => d.type === type)

    if (existingIndex >= 0) {
      diceToRoll[existingIndex].quantity += 1
      diceToRoll = [...diceToRoll] // Trigger reactivity
    } else {
      diceToRoll = [...diceToRoll, { type, sides, quantity: 1 }]
    }

    // Reset roll results when dice are modified
    rollResults = []
  }

  // Function to remove dice from roll
  function removeDice(index: number) {
    diceToRoll[index].quantity -= 1

    if (diceToRoll[index].quantity <= 0) {
      diceToRoll.splice(index, 1)
    }

    diceToRoll = [...diceToRoll] // Trigger reactivity

    // Reset roll results when dice are modified
    rollResults = []
  }

  // Function to clear all dice
  function clearDice() {
    diceToRoll = []

    // Reset roll results when dice are cleared
    rollResults = []
  }

  // Function to add custom dice
  function addCustomDice() {
    if (customSides < 2) {
      error = "Custom dice must have at least 2 sides"
      return
    }

    addDice(`d${customSides}`, customSides)
    error = null
  }

  function getRandomNumbers(min: number, max: number, count: number): number[] {
    return secureRandomIntegers(min, max, count)
  }

  // Function to roll the dice
  async function rollDice() {
    if (isRolling || diceToRoll.length === 0) return

    isRolling = true
    showConfetti = false
    error = null

    // Add rolling class to all dice
    document.querySelectorAll(".dice-container").forEach((dice) => {
      dice.classList.add("rolling")
    })

    try {
      // Prepare results array
      const newResults = []

      // Roll each type of dice
      for (const dice of diceToRoll) {
        // Get random numbers for this dice type
        const randomNumbers = getRandomNumbers(1, dice.sides, dice.quantity)

        // Calculate total
        const total = randomNumbers.reduce((sum, num) => sum + num, 0)

        // Add to results
        newResults.push({
          type: dice.type,
          sides: dice.sides,
          results: randomNumbers,
          total,
        })
      }

      // Update roll results
      rollResults = newResults

      // Add to history
      const totalSum = newResults.reduce((sum, result) => sum + result.total, 0)
      const diceString = diceToRoll
        .map((d) => `${d.quantity}${d.type}`)
        .join(" + ")

      rollHistory = [
        {
          timestamp: new Date().toLocaleTimeString(),
          dice: diceString,
          results: newResults.flatMap((r) => r.results),
          total: totalSum,
        },
        ...rollHistory.slice(0, 9), // Keep only the last 10 rolls
      ]

      // Play sound if enabled
      if (rollSound) {
        const audio = new Audio("/dice-roll.mp3")
        audio.play().catch((e) => console.error("Error playing sound:", e))
      }

      // Show confetti for high rolls (e.g., natural 20 on d20)
      const hasNatural20 = newResults.some(
        (result) => result.type === "d20" && result.results.includes(20),
      )

      if (hasNatural20) {
        showConfetti = true
      }
    } catch (err) {
      console.error("Error rolling dice:", err)
      error =
        err instanceof Error
          ? err.message
          : "Failed to roll dice. Please try again."
    } finally {
      // Simulate rolling animation
      setTimeout(() => {
        // Remove rolling class from all dice
        document.querySelectorAll(".dice-container").forEach((dice) => {
          dice.classList.remove("rolling")
        })

        isRolling = false
      }, rollSpeed)
    }
  }

  // Function to format dice for display
  function formatDiceString() {
    if (diceToRoll.length === 0) return "No dice selected"
    return diceToRoll.map((d) => `${d.quantity}${d.type}`).join(" + ")
  }

  // Function to calculate total of all dice
  function calculateTotal() {
    return rollResults.reduce((sum, result) => sum + result.total, 0)
  }

  // Function to get dice face dots based on value
  function getDiceDots(value: number) {
    switch (value) {
      case 1:
        return [{ top: "50%", left: "50%" }]
      case 2:
        return [
          { top: "25%", left: "25%" },
          { top: "75%", left: "75%" },
        ]
      case 3:
        return [
          { top: "25%", left: "25%" },
          { top: "50%", left: "50%" },
          { top: "75%", left: "75%" },
        ]
      case 4:
        return [
          { top: "25%", left: "25%" },
          { top: "25%", left: "75%" },
          { top: "75%", left: "25%" },
          { top: "75%", left: "75%" },
        ]
      case 5:
        return [
          { top: "25%", left: "25%" },
          { top: "25%", left: "75%" },
          { top: "50%", left: "50%" },
          { top: "75%", left: "25%" },
          { top: "75%", left: "75%" },
        ]
      case 6:
        return [
          { top: "25%", left: "25%" },
          { top: "25%", left: "50%" },
          { top: "25%", left: "75%" },
          { top: "75%", left: "25%" },
          { top: "75%", left: "50%" },
          { top: "75%", left: "75%" },
        ]
      default:
        return []
    }
  }

  // Function to determine if we should show animated dice
  function shouldShowAnimatedDice(sides: number) {
    return sides === 6 // Only show animated dice for d6
  }

  // Function to re-roll a single die
  async function reRollSingleDie(resultIndex: number, dieIndex: number) {
    if (isRolling) return

    // Get the die element
    const dieElement = document.querySelector(
      `.dice-container[data-result="${resultIndex}"][data-die="${dieIndex}"]`,
    )
    if (dieElement) {
      dieElement.classList.add("rolling")
    }

    try {
      // Get the die type
      const result = rollResults[resultIndex]

      // Roll a new value
      const newValue = getRandomNumbers(1, result.sides, 1)[0]

      // Update the result
      result.results[dieIndex] = newValue

      // Recalculate total
      result.total = result.results.reduce((sum, num) => sum + num, 0)

      // Trigger reactivity
      rollResults = [...rollResults]

      // Show confetti for natural 20
      if (result.type === "d20" && newValue === 20) {
        showConfetti = true
      }
    } catch (err) {
      console.error("Error re-rolling die:", err)
    } finally {
      // Remove rolling class after animation
      setTimeout(() => {
        if (dieElement) {
          dieElement.classList.remove("rolling")
        }
      }, rollSpeed)
    }
  }
</script>

<svelte:head>
  <title
    >Dice Roller - Roll virtual dice online for games, D&D, and more | Promo Amp</title
  >
  <meta
    name="description"
    content="Free online dice roller tool. Roll virtual dice for tabletop games, D&D, board games, or any game that requires dice. Supports d4, d6, d8, d10, d12, d20, d100, and custom dice."
  />
  <meta
    name="keywords"
    content="dice roller, virtual dice, online dice, d20, dungeons and dragons, tabletop games, random dice, d&d dice"
  />
  <meta
    property="og:title"
    content="Dice Roller - Roll virtual dice online for games, D&D, and more | Promo Amp"
  />
  <meta
    property="og:description"
    content="Roll virtual dice for tabletop games, D&D, board games, or any game that requires dice. Supports all standard dice types and custom dice."
  />
  <meta property="og:type" content="website" />
  <meta property="og:image" content="https://img.promoamp.com/og_image.png" />
  <meta name="twitter:card" content="summary_large_image" />
  <meta
    name="twitter:title"
    content="Dice Roller - Roll virtual dice online for games, D&D, and more | Promo Amp"
  />
  <meta
    name="twitter:description"
    content="Roll virtual dice for tabletop games, D&D, board games, or any game that requires dice. Supports all standard dice types and custom dice."
  />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://www.promoamp.com/dice-roller" />
</svelte:head>

<div class="container mx-auto py-8 px-4 mt-20">
  <h1 class="text-3xl font-bold mb-6 text-center">Online Dice Roller</h1>

  <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
    <!-- Left Column: Dice Selection -->
    <Card.Root class="shadow-sm">
      <Card.Header>
        <Card.Title
          ><h2 class="text-2xl font-bold">Add Dice to Roll</h2></Card.Title
        >
        <Card.Description>Choose the dice you want to roll</Card.Description>
      </Card.Header>
      <Card.Content>
        <Tabs bind:value={activeTab} class="w-full">
          <TabsList class="grid grid-cols-2 mb-4">
            <TabsTrigger value="standard">Standard Dice</TabsTrigger>
            <TabsTrigger value="custom">Custom Dice</TabsTrigger>
          </TabsList>

          <TabsContent value="standard" class="space-y-4">
            <div class="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {#each standardDice as dice}
                <Button
                  variant="outline"
                  class="h-16 flex flex-col items-center justify-center gap-1"
                  onclick={() => addDice(dice.name, dice.sides)}
                >
                  <Dice class="h-5 w-5" />
                  <span class="text-sm font-bold">{dice.name}</span>
                </Button>
              {/each}
            </div>
          </TabsContent>

          <TabsContent value="custom" class="space-y-4">
            <div class="space-y-4">
              <div>
                <Label for="custom-sides">Number of Sides</Label>
                <div class="flex items-center gap-2">
                  <Input
                    id="custom-sides"
                    type="number"
                    min="2"
                    max="1000"
                    class="w-full"
                    bind:value={customSides}
                  />
                </div>
              </div>

              <Button variant="default" class="w-full" onclick={addCustomDice}>
                Add d{customSides}
              </Button>

              {#if error}
                <div class="text-red-500 text-sm">{error}</div>
              {/if}
            </div>
          </TabsContent>
        </Tabs>

        <div class="mt-6">
          <h3 class="text-lg font-semibold mb-2">Selected Dice</h3>

          {#if diceToRoll.length === 0}
            <div class="text-gray-500 text-center py-4">
              No dice selected. Choose dice above to get started.
            </div>
          {:else}
            <div class="space-y-2">
              {#each diceToRoll as dice, i}
                <div
                  class="flex items-center justify-between bg-muted p-2 rounded-md"
                >
                  <div class="flex items-center gap-2">
                    <Dice class="h-5 w-5" />
                    <span>{dice.quantity}× {dice.type}</span>
                  </div>
                  <div class="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      class="h-8 w-8 p-0"
                      onclick={() => addDice(dice.type, dice.sides)}
                    >
                      <Plus class="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      class="h-8 w-8 p-0"
                      onclick={() => removeDice(i)}
                    >
                      <Minus class="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              {/each}

              <div class="flex justify-between mt-4">
                <Button variant="outline" size="sm" onclick={clearDice}>
                  Clear All
                </Button>
                <div class="text-sm font-medium">
                  {formatDiceString()}
                </div>
              </div>
            </div>
          {/if}
        </div>

        <div class="mt-6 space-y-4">
          <div class="flex items-center justify-between">
            <Label for="roll-sound" class="cursor-pointer">Roll Sound</Label>
            <Switch id="roll-sound" bind:checked={rollSound} />
          </div>

          <div>
            <Label for="roll-speed">Roll Animation Speed</Label>
            <div class="flex items-center gap-2">
              <span class="text-xs">Fast</span>
              <Input
                id="roll-speed"
                type="number"
                min={500}
                max={2000}
                step={100}
                class="w-full"
                bind:value={rollSpeed}
              />
              <span class="text-xs">Slow</span>
            </div>
          </div>
        </div>
      </Card.Content>
    </Card.Root>

    <!-- Right Column: Dice Results -->
    <Card.Root class="shadow-sm">
      <Card.Header>
        <Card.Title><h2 class="text-2xl font-bold">Roll Results</h2></Card.Title
        >
        <Card.Description>
          Click the button below to roll your selected dice
        </Card.Description>
      </Card.Header>
      <Card.Content class="flex flex-col items-center justify-center">
        {#if showConfetti}
          <Confetti />
        {/if}

        <div class="w-full mb-6">
          <div class="bg-muted rounded-lg p-6 text-center mb-4">
            <div class="flex flex-wrap justify-center gap-3 mb-4">
              {#if diceToRoll.length > 0}
                {#if rollResults.length > 0 && !isRolling}
                  {#each rollResults as result}
                    {#each result.results as roll}
                      <div>
                        {#if shouldShowAnimatedDice(result.sides) && roll <= 6}
                          <div class="dice-container">
                            <div class="dice">
                              <!-- Front face -->
                              <div class="dice-face front">
                                {#each getDiceDots(roll) as dot}
                                  <div
                                    class="dot"
                                    style="top: {dot.top}; left: {dot.left};"
                                  ></div>
                                {/each}
                              </div>
                              <!-- Back face (opposite of front) -->
                              <div class="dice-face back">
                                {#each getDiceDots(7 - roll) as dot}
                                  <div
                                    class="dot"
                                    style="top: {dot.top}; left: {dot.left};"
                                  ></div>
                                {/each}
                              </div>
                              <!-- Right face -->
                              <div class="dice-face right">
                                {#each getDiceDots(((roll + 1) % 6) + 1) as dot}
                                  <div
                                    class="dot"
                                    style="top: {dot.top}; left: {dot.left};"
                                  ></div>
                                {/each}
                              </div>
                              <!-- Left face -->
                              <div class="dice-face left">
                                {#each getDiceDots(((roll + 2) % 6) + 1) as dot}
                                  <div
                                    class="dot"
                                    style="top: {dot.top}; left: {dot.left};"
                                  ></div>
                                {/each}
                              </div>
                              <!-- Top face -->
                              <div class="dice-face top">
                                {#each getDiceDots(((roll + 3) % 6) + 1) as dot}
                                  <div
                                    class="dot"
                                    style="top: {dot.top}; left: {dot.left};"
                                  ></div>
                                {/each}
                              </div>
                              <!-- Bottom face -->
                              <div class="dice-face bottom">
                                {#each getDiceDots(((roll + 4) % 6) + 1) as dot}
                                  <div
                                    class="dot"
                                    style="top: {dot.top}; left: {dot.left};"
                                  ></div>
                                {/each}
                              </div>
                            </div>
                          </div>
                        {:else}
                          <div class="dice-box">
                            <span>{roll}</span>
                          </div>
                        {/if}
                      </div>
                    {/each}
                  {/each}
                {:else}
                  {#each diceToRoll as dice}
                    {#each [...Array(dice.quantity).keys()] as dieIndex}
                      <div aria-label={`${dice.type} ${dieIndex + 1}`}>
                        {#if shouldShowAnimatedDice(dice.sides)}
                          <div
                            class={isRolling
                              ? "dice-container rolling"
                              : "dice-container"}
                          >
                            <div class="dice">
                              <!-- Front face -->
                              <div class="dice-face front">
                                {#each getDiceDots(isRolling ? 6 : 6) as dot}
                                  <div
                                    class="dot"
                                    style="top: {dot.top}; left: {dot.left};"
                                  ></div>
                                {/each}
                              </div>
                              <!-- Back face -->
                              <div class="dice-face back">
                                <div
                                  class="dot"
                                  style="top: 50%; left: 50%;"
                                ></div>
                              </div>
                              <!-- Right face -->
                              <div class="dice-face right">
                                {#each getDiceDots(3) as dot}
                                  <div
                                    class="dot"
                                    style="top: {dot.top}; left: {dot.left};"
                                  ></div>
                                {/each}
                              </div>
                              <!-- Left face -->
                              <div class="dice-face left">
                                {#each getDiceDots(4) as dot}
                                  <div
                                    class="dot"
                                    style="top: {dot.top}; left: {dot.left};"
                                  ></div>
                                {/each}
                              </div>
                              <!-- Top face -->
                              <div class="dice-face top">
                                {#each getDiceDots(2) as dot}
                                  <div
                                    class="dot"
                                    style="top: {dot.top}; left: {dot.left};"
                                  ></div>
                                {/each}
                              </div>
                              <!-- Bottom face -->
                              <div class="dice-face bottom">
                                {#each getDiceDots(5) as dot}
                                  <div
                                    class="dot"
                                    style="top: {dot.top}; left: {dot.left};"
                                  ></div>
                                {/each}
                              </div>
                            </div>
                          </div>
                        {:else}
                          <div
                            class={isRolling ? "dice-box rolling" : "dice-box"}
                          >
                            <span>{dice.type}</span>
                          </div>
                        {/if}
                      </div>
                    {/each}
                  {/each}
                {/if}
              {:else}
                <div class="text-gray-400 italic">
                  Add dice to see them here
                </div>
              {/if}
            </div>

            {#if isRolling}
              <div class="text-2xl font-bold animate-pulse mb-2">
                Dice are rolling...
              </div>
            {:else if rollResults.length === 0}
              <div class="text-xl text-gray-500 mb-2">
                Results will appear here
              </div>
              {#if diceToRoll.length > 0}
                <div class="text-sm text-gray-500">
                  Click "Roll Dice" to roll {formatDiceString()}
                </div>
              {/if}
            {:else}
              <div class="text-4xl font-bold mb-2">{calculateTotal()}</div>
              <div class="text-sm text-gray-500">{formatDiceString()}</div>
            {/if}
          </div>

          {#if !isRolling && rollResults.length > 0}
            <div class="space-y-4">
              <div class="text-sm text-gray-500 mb-2 italic text-center">
                Tip: Click on any individual die to re-roll just that one!
              </div>
              {#each rollResults as result, i}
                <div class="border rounded-md p-3">
                  <div class="flex justify-between items-center mb-2">
                    <span class="font-semibold"
                      >{result.results.length}× {result.type}</span
                    >
                    <span class="font-bold">Total: {result.total}</span>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    {#each result.results as roll, dieIndex}
                      {#if shouldShowAnimatedDice(result.sides) && roll <= 6}
                        <div
                          class="dice-container"
                          data-result={i}
                          data-die={dieIndex}
                          onclick={() => reRollSingleDie(i, dieIndex)}
                          onkeydown={(e) =>
                            e.key === "Enter" && reRollSingleDie(i, dieIndex)}
                          role="button"
                          tabindex="0"
                          title="Click to re-roll this die"
                        >
                          <div class="dice">
                            <!-- Front face -->
                            <div class="dice-face front">
                              {#each getDiceDots(roll) as dot}
                                <div
                                  class="dot"
                                  style="top: {dot.top}; left: {dot.left};"
                                ></div>
                              {/each}
                            </div>
                            <!-- Back face (opposite of front) -->
                            <div class="dice-face back">
                              {#each getDiceDots(7 - roll) as dot}
                                <div
                                  class="dot"
                                  style="top: {dot.top}; left: {dot.left};"
                                ></div>
                              {/each}
                            </div>
                            <!-- Right face -->
                            <div class="dice-face right">
                              {#each getDiceDots(((roll + 1) % 6) + 1) as dot}
                                <div
                                  class="dot"
                                  style="top: {dot.top}; left: {dot.left};"
                                ></div>
                              {/each}
                            </div>
                            <!-- Left face -->
                            <div class="dice-face left">
                              {#each getDiceDots(((roll + 2) % 6) + 1) as dot}
                                <div
                                  class="dot"
                                  style="top: {dot.top}; left: {dot.left};"
                                ></div>
                              {/each}
                            </div>
                            <!-- Top face -->
                            <div class="dice-face top">
                              {#each getDiceDots(((roll + 3) % 6) + 1) as dot}
                                <div
                                  class="dot"
                                  style="top: {dot.top}; left: {dot.left};"
                                ></div>
                              {/each}
                            </div>
                            <!-- Bottom face -->
                            <div class="dice-face bottom">
                              {#each getDiceDots(((roll + 4) % 6) + 1) as dot}
                                <div
                                  class="dot"
                                  style="top: {dot.top}; left: {dot.left};"
                                ></div>
                              {/each}
                            </div>
                          </div>
                        </div>
                      {:else}
                        <div
                          class="w-10 h-10 flex items-center justify-center rounded-md border-2 font-bold text-sm cursor-pointer"
                          class:bg-green-100={roll === result.sides}
                          class:border-green-500={roll === result.sides}
                          class:bg-red-100={roll === 1}
                          class:border-red-500={roll === 1}
                          onclick={() => reRollSingleDie(i, dieIndex)}
                          onkeydown={(e) =>
                            e.key === "Enter" && reRollSingleDie(i, dieIndex)}
                          role="button"
                          tabindex="0"
                          title="Click to re-roll this die"
                        >
                          {roll}
                        </div>
                      {/if}
                    {/each}
                  </div>
                </div>
              {/each}
            </div>
          {/if}
        </div>

        <Button
          disabled={isRolling || diceToRoll.length === 0}
          onclick={rollDice}
          class="flex items-center gap-2 w-full sm:w-auto px-6 py-2"
        >
          <Dice class="h-5 w-5" />
          {isRolling ? "Rolling the dice..." : "Roll Dice"}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          class="mt-4"
          onclick={() => (showHistory = !showHistory)}
        >
          {showHistory ? "Hide History" : "Show History"}
        </Button>

        {#if showHistory && rollHistory.length > 0}
          <div
            class="w-full mt-4 border rounded-md p-3 max-h-60 overflow-y-auto"
          >
            <h3 class="font-semibold mb-2">Roll History</h3>
            <div class="space-y-2 text-sm">
              {#each rollHistory as roll}
                <div class="flex justify-between items-center border-b pb-1">
                  <div>
                    <span class="text-gray-500">{roll.timestamp}</span>
                    <span class="ml-2">{roll.dice}-sided</span>
                  </div>
                  <span class="font-bold">{roll.total}</span>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      </Card.Content>
    </Card.Root>
  </div>

  <!-- Information Section -->
  <div class="mt-12 prose max-w-5xl mx-auto">
    <section id="about" class="mb-8">
      <h3 class="text-xl font-bold mb-2">About the Online Dice Roller</h3>
      <p>
        Our free online dice roller lets you roll virtual dice for tabletop
        games, board games, role-playing games like Dungeons & Dragons, or any
        other game that requires dice. This tool supports all standard dice
        types (d4, d6, d8, d10, d12, d20, d100) as well as custom dice with any
        number of sides.
      </p>
    </section>

    <section id="features" class="mb-8">
      <h3 class="text-xl font-bold mb-2">Features</h3>
      <ul class="list-disc list-inside">
        <li>
          <b>Multiple Dice Types:</b> Roll any combination of standard dice (d4,
          d6, d8, d10, d12, d20, d100).
        </li>
        <li>
          <b>Custom Dice:</b> Create custom dice with any number of sides from 2
          to 1000.
        </li>
        <li>
          <b>Multiple Dice:</b> Roll multiple dice at once and see individual results
          and totals.
        </li>
        <li>
          <b>Secure Random Values:</b> Uses the browser's Web Crypto API and rejection
          sampling to produce each die value.
        </li>
        <li><b>Roll History:</b> Keep track of your previous rolls.</li>
        <li>
          <b>Customizable:</b> Adjust roll animation speed and toggle sound effects.
        </li>
        <li>
          <b>Visual Feedback:</b> Easily identify critical hits (maximum rolls) and
          critical fails (rolls of 1).
        </li>
        <li>
          <b>Animated Dice:</b> Realistic 3D animated dice for standard six-sided
          dice.
        </li>
        <li>
          <b>Individual Re-rolls:</b> Click on any die to re-roll just that one without
          affecting other dice.
        </li>
      </ul>
    </section>

    <section id="how-to-use" class="mb-8">
      <h3 class="text-xl font-bold mb-2">How to Use the Dice Roller</h3>
      <ol class="list-decimal list-inside">
        <li>
          Select the type of dice you want to roll from the "Standard Dice" tab.
        </li>
        <li>
          For custom dice, switch to the "Custom Dice" tab, set the number of
          sides, and click "Add".
        </li>
        <li>
          Adjust the quantity of each dice type using the + and - buttons.
        </li>
        <li>Click the "Roll Dice" button to roll all selected dice.</li>
        <li>View your results in the "Roll Results" section.</li>
        <li>Use the "Show History" button to see your previous rolls.</li>
      </ol>
    </section>

    <section id="applications" class="mb-8">
      <h3 class="text-xl font-bold mb-2">Applications of the Dice Roller</h3>
      <ul class="list-disc list-inside">
        <li>
          <b>Tabletop RPGs:</b> Perfect for Dungeons & Dragons, Pathfinder, and other
          role-playing games.
        </li>
        <li><b>Board Games:</b> Use for any board game that requires dice.</li>
        <li>
          <b>Game Development:</b> Test probability distributions for game mechanics.
        </li>
        <li>
          <b>Education:</b> Demonstrate probability concepts in mathematics.
        </li>
        <li>
          <b>Decision Making:</b> Let the dice decide when you can't make up your
          mind!
        </li>
      </ul>
    </section>
  </div>
</div>

<style>
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  .animate-pulse {
    animation: pulse 1.5s ease infinite;
  }

  /* Dice styling */
  .dice-container {
    width: 50px;
    height: 50px;
    perspective: 600px;
    margin: 5px;
    cursor: pointer;
  }

  @keyframes placeholder-roll {
    0% {
      transform: rotate(0deg) scale(1);
      background-position: 0% 0%;
    }
    25% {
      transform: rotate(-15deg) scale(1.1);
      background-position: 25% 25%;
    }
    50% {
      transform: rotate(0deg) scale(1);
      background-position: 50% 50%;
    }
    75% {
      transform: rotate(15deg) scale(1.1);
      background-position: 75% 75%;
    }
    100% {
      transform: rotate(0deg) scale(1);
      background-position: 100% 100%;
    }
  }

  .dice {
    width: 100%;
    height: 100%;
    position: relative;
    transform-style: preserve-3d;
    transition: transform 0.5s;
  }

  .dice-face {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: white;
    border: 2px solid #333;
    border-radius: 5%;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.1);
    backface-visibility: visible;
  }

  /* Position each face */
  .dice-face.front {
    transform: translateZ(25px);
  }

  .dice-face.back {
    transform: rotateY(180deg) translateZ(25px);
  }

  .dice-face.right {
    transform: rotateY(90deg) translateZ(25px);
  }

  .dice-face.left {
    transform: rotateY(-90deg) translateZ(25px);
  }

  .dice-face.top {
    transform: rotateX(90deg) translateZ(25px);
  }

  .dice-face.bottom {
    transform: rotateX(-90deg) translateZ(25px);
  }

  .dot {
    position: absolute;
    width: 10px;
    height: 10px;
    background-color: #333;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    box-shadow: inset 0 0 2px rgba(0, 0, 0, 0.8);
  }

  /* Hover effect */
  .dice-container:hover .dice {
    transform: rotateX(20deg) rotateY(20deg);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }

  /* Animation for dice rolling */
  @keyframes roll {
    0% {
      transform: rotateX(0deg) rotateY(0deg) rotateZ(0deg);
    }
    25% {
      transform: rotateX(90deg) rotateY(45deg) rotateZ(45deg);
    }
    50% {
      transform: rotateX(180deg) rotateY(90deg) rotateZ(90deg);
    }
    75% {
      transform: rotateX(270deg) rotateY(135deg) rotateZ(135deg);
    }
    100% {
      transform: rotateX(360deg) rotateY(180deg) rotateZ(180deg);
    }
  }

  /* Enhanced rolling animation with more dynamic movement */
  @keyframes enhanced-roll {
    0% {
      transform: rotateX(0deg) rotateY(0deg) rotateZ(0deg);
    }
    10% {
      transform: rotateX(45deg) rotateY(90deg) rotateZ(22.5deg);
    }
    20% {
      transform: rotateX(90deg) rotateY(180deg) rotateZ(45deg);
    }
    30% {
      transform: rotateX(135deg) rotateY(270deg) rotateZ(67.5deg);
    }
    40% {
      transform: rotateX(180deg) rotateY(360deg) rotateZ(90deg);
    }
    50% {
      transform: rotateX(225deg) rotateY(450deg) rotateZ(112.5deg);
    }
    60% {
      transform: rotateX(270deg) rotateY(540deg) rotateZ(135deg);
    }
    70% {
      transform: rotateX(315deg) rotateY(630deg) rotateZ(157.5deg);
    }
    80% {
      transform: rotateX(360deg) rotateY(720deg) rotateZ(180deg);
    }
    90% {
      transform: rotateX(405deg) rotateY(810deg) rotateZ(202.5deg);
    }
    100% {
      transform: rotateX(450deg) rotateY(900deg) rotateZ(225deg);
    }
  }

  /* Apply animation when rolling */
  :global(.rolling .dice) {
    animation: enhanced-roll 1.2s cubic-bezier(0.3, 0, 0.7, 1) infinite;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.3);
    transform-origin: center center;
  }

  /* Add a subtle glow effect to dice when rolling */
  @keyframes glow {
    0%,
    100% {
      box-shadow:
        0 0 5px rgba(255, 255, 255, 0.5),
        0 0 10px rgba(0, 0, 0, 0.3);
    }
    50% {
      box-shadow:
        0 0 10px rgba(255, 255, 255, 0.8),
        0 0 15px rgba(0, 0, 0, 0.5);
    }
  }

  /* Add a subtle shadow to enhance 3D effect */
  .dice::before {
    content: "";
    position: absolute;
    width: 100%;
    height: 20px;
    bottom: -20px;
    left: 0;
    background: radial-gradient(
      ellipse at center,
      rgba(0, 0, 0, 0.3) 0%,
      rgba(0, 0, 0, 0) 70%
    );
    border-radius: 50%;
    z-index: -1;
    transform: rotateX(90deg) translateZ(-10px);
    filter: blur(3px);
    opacity: 0.7;
  }

  :global(.rolling .dice-face) {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.4);
    border-color: #555;
    animation: glow 0.8s ease-in-out infinite;
  }

  /* Bounce effect when dice stops rolling */
  @keyframes bounce-settle {
    0% {
      transform: translateY(-5px) rotateX(10deg);
    }
    50% {
      transform: translateY(2px) rotateX(-5deg);
    }
    100% {
      transform: translateY(0) rotateX(0deg);
    }
  }

  /* Apply bounce animation when dice stops rolling */
  .dice-container:not(.rolling) .dice {
    animation: bounce-settle 0.5s ease-out forwards;
  }

  /* Add a slight wobble effect to the dice container when rolling */
  @keyframes wobble {
    0%,
    100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-2px);
    }
    75% {
      transform: translateX(2px);
    }
  }

  :global(.rolling) {
    animation: wobble 0.3s ease-in-out infinite;
  }

  /* Dice box for non-d6 dice */
  .dice-box {
    width: 45px;
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 2px solid #333;
    border-radius: 15%;
    font-weight: bold;
    font-size: 0.9rem;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
    margin: 5px;
    position: relative;
    transform: rotate(5deg);
    transition: transform 0.3s ease;
  }

  .dice-box:hover {
    transform: rotate(0) scale(1.05);
  }

  .dice-box.rolling {
    animation: box-roll 0.8s ease-in-out infinite;
  }

  @keyframes box-roll {
    0% {
      transform: rotate(0deg) scale(1);
    }
    25% {
      transform: rotate(-15deg) scale(1.1);
    }
    50% {
      transform: rotate(0deg) scale(1);
    }
    75% {
      transform: rotate(15deg) scale(1.1);
    }
    100% {
      transform: rotate(0deg) scale(1);
    }
  }
</style>
